import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import Slider from "@material-ui/core/Slider";
import {
  ISearchFilter,
  ISearchFilterSet,
} from "../../Foundations/Types/ISearchFilter";
import SearchFilterName from "../../Foundations/Enum/SearchFilterName";
import searchFilterContext from "../../Foundations/Contexts/SearchFilterContext";
import { INumberValueLabelPair } from "../../Foundations/Types/IGenericObject";
import { getSelectedItemsValue } from "../../Foundations/Utils/SearchFilterUtils";
import {
  DEFAULT_SEARCH_RADIUS,
  MAX_RADIUS,
} from "../../Foundations/Constants/SearchRadius";

interface IProps {
  drawCircle(map: google.maps.Map, radius: number): void;
  mapInstance: google.maps.Map;
  setCircleRadius(radius: number): void;
}

const MapViewSlider: React.FC<IProps> = ({
  mapInstance,
  drawCircle,
  setCircleRadius,
}) => {
  const { activeFilters, availableFilters, setActiveFilters } =
    useContext(searchFilterContext);
  const [useCustomRadius, setUseCustomRadius] = useState<boolean>(false);
  const [customRadius, setCustomRadius] = useState<number>(
    DEFAULT_SEARCH_RADIUS,
  );
  const [radiusData, setRadiusData] = useState<INumberValueLabelPair[]>([]);
  const getSliderValue = useCallback(() => {
    if (activeFilters === null) {
      return DEFAULT_SEARCH_RADIUS;
    }

    const valueFromActiveFilters = getSelectedItemsValue(
      activeFilters[SearchFilterName.RADIUS],
    );

    return valueFromActiveFilters !== null &&
      typeof valueFromActiveFilters[0] === "number"
      ? valueFromActiveFilters[0]
      : DEFAULT_SEARCH_RADIUS;
  }, [activeFilters]);

  useEffect(() => {
    if (activeFilters === null) {
      setCircleRadius(DEFAULT_SEARCH_RADIUS * 1000);
    } else {
      const newRadius: number = getSliderValue();
      setCircleRadius(newRadius * 1000);
    }
  }, [setCircleRadius, activeFilters, getSliderValue]);

  useEffect(() => {
    if (availableFilters === null) {
      return;
    }

    const radiusFilter: ISearchFilter | undefined =
      availableFilters[SearchFilterName.RADIUS];

    if (
      typeof radiusFilter !== "undefined" &&
      typeof radiusFilter.items !== "undefined"
    ) {
      const newRadiusData: INumberValueLabelPair[] = radiusFilter.items.map(
        (item) => {
          return {
            label: item.label,
            value: item.value as number,
          };
        },
      );
      setRadiusData(newRadiusData);
    }
  }, [availableFilters]);

  const handleSliderChange = (
    event: ChangeEvent<{}>,
    value: number | number[],
  ) => {
    event.preventDefault();
    if (typeof value === "number") {
      drawCircle(mapInstance, value * 1000);
      setCircleRadius(value * 1000);
    }

    if (availableFilters === null) {
      return;
    }

    const radiusFilter: ISearchFilter = {
      ...availableFilters[SearchFilterName.RADIUS],
    };

    if (typeof radiusFilter.items !== "undefined") {
      radiusFilter.items = radiusFilter.items.map((item) => {
        item.selected = item.value === value;
        return item;
      });
    }

    let newActiveFilters: ISearchFilterSet = { ...activeFilters };
    newActiveFilters[SearchFilterName.RADIUS] = radiusFilter;
    newActiveFilters[SearchFilterName.RADIUS].hasSelection = true;
    setActiveFilters(newActiveFilters);
  };

  const handleCustomRadiusChange = (event: ChangeEvent<HTMLInputElement>) => {
    let hasSelection = false;
    let newVal = parseInt(event.target.value);

    if (availableFilters === null || isNaN(newVal)) {
      return;
    }

    newVal = Math.min(MAX_RADIUS, Math.max(0, newVal));

    const radiusFilter: ISearchFilter = {
      ...availableFilters[SearchFilterName.RADIUS],
    };

    if (typeof radiusFilter.items !== "undefined") {
      radiusFilter.items = radiusFilter.items.map((item) => {
        item.selected = item.value === newVal;
        if (item.value === newVal) {
          hasSelection = true;
        }
        return item;
      });
    }

    if (!hasSelection && radiusFilter.items !== undefined) {
      radiusFilter.items.push({
        label: `Custom radius`,
        value: newVal,
        selected: true,
      })
    }

    let newActiveFilters: ISearchFilterSet = { ...activeFilters };
    newActiveFilters[SearchFilterName.RADIUS] = radiusFilter;
    newActiveFilters[SearchFilterName.RADIUS].hasSelection = true;
    setActiveFilters(newActiveFilters);
    drawCircle(mapInstance, newVal * 1000);
    setCircleRadius(newVal * 1000);
    setCustomRadius(newVal);
  };

  return (
    <div className="bg-white py-5 px-6 flex">
      {radiusData.length > 0 && (
        <React.Fragment>
          <div className="w-3/12 flex items-center">
            <span
              className="inline-block cursor-pointer"
              onClick={() => setUseCustomRadius(!useCustomRadius)}
            >
              RADIUS 
            </span>
          </div>
          <div className="w-9/12">
            {!useCustomRadius && (
              <Slider
                defaultValue={radiusData[radiusData.length - 1].value}
                onChange={handleSliderChange}
                value={getSliderValue()}
                aria-labelledby="discrete-slider-always"
                step={null}
                max={radiusData[radiusData.length - 1].value}
                min={radiusData[0].value}
                marks={radiusData}
              />
            )}
            {useCustomRadius && (
              <input
                type="number"
                className="w-20 py-3 border border-gray-300 rounded-md px-2"
                value={customRadius}
                onChange={handleCustomRadiusChange}
              />
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default MapViewSlider;
